import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

const THEMES = {
  midnight: {
    name: 'Midnight',
    description: 'Classic dark with emerald accents',
    preview: ['#0a0d1f', '#161b22', '#00ff88'],
    vars: {
      '--bg-deepest': '#080b12',
      '--bg-main': '#0a0d1f',
      '--bg-card': 'rgba(22, 27, 34, 0.6)',
      '--bg-card-hover': 'rgba(28, 33, 40, 0.7)',
      '--bg-elevated': 'rgba(28, 33, 40, 0.85)',
      '--border-color': 'rgba(255, 255, 255, 0.06)',
      '--border-glow': 'rgba(0, 255, 136, 0.22)',
      '--text-primary': '#e6edf3',
      '--text-secondary': '#8b949e',
      '--text-dim': '#484f58',
      '--accent-primary': '#00ff88',
      '--accent-secondary': '#a855f7',
      '--accent-tertiary': '#3b82f6',
      '--shadow-card': '0 4px 24px rgba(0, 0, 0, 0.4)',
      '--shadow-glow': 'rgba(0, 255, 136, 0.15)',
      '--sidebar-bg': 'rgba(10, 13, 31, 0.95)',
      '--sidebar-accent': '#00ff88',
      '--gradient-primary': 'linear-gradient(135deg, #00ff88, #00cc6a)',
      '--gradient-secondary': 'linear-gradient(135deg, #a855f7, #8b5cf6)',
    },
  },
  ocean: {
    name: 'Ocean Deep',
    description: 'Deep navy with cyan waves',
    preview: ['#0a0d1f', '#131a3d', '#06b6d4'],
    vars: {
      '--bg-deepest': '#070a18',
      '--bg-main': '#0a0d1f',
      '--bg-card': 'rgba(15, 20, 50, 0.55)',
      '--bg-card-hover': 'rgba(20, 28, 65, 0.65)',
      '--bg-elevated': 'rgba(19, 26, 61, 0.8)',
      '--border-color': 'rgba(100, 150, 255, 0.08)',
      '--border-glow': 'rgba(6, 182, 212, 0.25)',
      '--text-primary': '#e8eef5',
      '--text-secondary': '#7b8baa',
      '--text-dim': '#4a5578',
      '--accent-primary': '#06b6d4',
      '--accent-secondary': '#3b82f6',
      '--accent-tertiary': '#8b5cf6',
      '--shadow-card': '0 4px 24px rgba(0, 0, 0, 0.4)',
      '--shadow-glow': 'rgba(6, 182, 212, 0.12)',
      '--sidebar-bg': 'rgba(7, 10, 24, 0.95)',
      '--sidebar-accent': '#06b6d4',
      '--gradient-primary': 'linear-gradient(135deg, #06b6d4, #0284c7)',
      '--gradient-secondary': 'linear-gradient(135deg, #3b82f6, #6366f1)',
    },
  },
  purple: {
    name: 'Purple Haze',
    description: 'Violet dream with pink neon',
    preview: ['#0f0a1e', '#1a1230', '#c084fc'],
    vars: {
      '--bg-deepest': '#0a0714',
      '--bg-main': '#0f0a1e',
      '--bg-card': 'rgba(26, 18, 48, 0.6)',
      '--bg-card-hover': 'rgba(30, 20, 55, 0.7)',
      '--bg-elevated': 'rgba(26, 18, 53, 0.85)',
      '--border-color': 'rgba(200, 132, 252, 0.1)',
      '--border-glow': 'rgba(192, 132, 252, 0.25)',
      '--text-primary': '#f0e8ff',
      '--text-secondary': '#9b8bbb',
      '--text-dim': '#5a4d70',
      '--accent-primary': '#c084fc',
      '--accent-secondary': '#ec4899',
      '--accent-tertiary': '#6366f1',
      '--shadow-card': '0 4px 24px rgba(0, 0, 0, 0.5)',
      '--shadow-glow': 'rgba(192, 132, 252, 0.12)',
      '--sidebar-bg': 'rgba(10, 7, 20, 0.95)',
      '--sidebar-accent': '#c084fc',
      '--gradient-primary': 'linear-gradient(135deg, #c084fc, #a855f7)',
      '--gradient-secondary': 'linear-gradient(135deg, #ec4899, #f43f5e)',
    },
  },
  emerald: {
    name: 'Emerald Dream',
    description: 'Rich emerald with teal glow',
    preview: ['#0a1410', '#0f1f1a', '#10b981'],
    vars: {
      '--bg-deepest': '#050f0a',
      '--bg-main': '#0a1410',
      '--bg-card': 'rgba(15, 31, 26, 0.6)',
      '--bg-card-hover': 'rgba(16, 38, 30, 0.7)',
      '--bg-elevated': 'rgba(15, 31, 26, 0.85)',
      '--border-color': 'rgba(16, 185, 129, 0.1)',
      '--border-glow': 'rgba(16, 185, 129, 0.25)',
      '--text-primary': '#e8f5f0',
      '--text-secondary': '#7b9e8e',
      '--text-dim': '#4a5c54',
      '--accent-primary': '#10b981',
      '--accent-secondary': '#14b8a6',
      '--accent-tertiary': '#06b6d4',
      '--shadow-card': '0 4px 24px rgba(0, 0, 0, 0.4)',
      '--shadow-glow': 'rgba(16, 185, 129, 0.12)',
      '--sidebar-bg': 'rgba(5, 15, 10, 0.95)',
      '--sidebar-accent': '#10b981',
      '--gradient-primary': 'linear-gradient(135deg, #10b981, #059669)',
      '--gradient-secondary': 'linear-gradient(135deg, #14b8a6, #0d9488)',
    },
  },
  sunset: {
    name: 'Sunset Blaze',
    description: 'Warm orange to pink gradient',
    preview: ['#1a0f0a', '#2d1810', '#f97316'],
    vars: {
      '--bg-deepest': '#0f0705',
      '--bg-main': '#1a0f0a',
      '--bg-card': 'rgba(45, 24, 16, 0.6)',
      '--bg-card-hover': 'rgba(55, 30, 20, 0.7)',
      '--bg-elevated': 'rgba(45, 24, 16, 0.85)',
      '--border-color': 'rgba(249, 115, 22, 0.1)',
      '--border-glow': 'rgba(249, 115, 22, 0.25)',
      '--text-primary': '#fff5ed',
      '--text-secondary': '#c9a898',
      '--text-dim': '#7d6558',
      '--accent-primary': '#f97316',
      '--accent-secondary': '#ec4899',
      '--accent-tertiary': '#f43f5e',
      '--shadow-card': '0 4px 24px rgba(0, 0, 0, 0.4)',
      '--shadow-glow': 'rgba(249, 115, 22, 0.12)',
      '--sidebar-bg': 'rgba(15, 7, 5, 0.95)',
      '--sidebar-accent': '#f97316',
      '--gradient-primary': 'linear-gradient(135deg, #f97316, #ea580c)',
      '--gradient-secondary': 'linear-gradient(135deg, #ec4899, #f43f5e)',
    },
  },
  rose: {
    name: 'Rose Gold',
    description: 'Elegant rose with gold shimmer',
    preview: ['#1a0f14', '#2d1a24', '#f43f5e'],
    vars: {
      '--bg-deepest': '#0f070a',
      '--bg-main': '#1a0f14',
      '--bg-card': 'rgba(45, 26, 36, 0.6)',
      '--bg-card-hover': 'rgba(55, 32, 44, 0.7)',
      '--bg-elevated': 'rgba(45, 26, 36, 0.85)',
      '--border-color': 'rgba(244, 63, 94, 0.1)',
      '--border-glow': 'rgba(244, 63, 94, 0.25)',
      '--text-primary': '#fff0f5',
      '--text-secondary': '#c9a0b0',
      '--text-dim': '#7d5864',
      '--accent-primary': '#f43f5e',
      '--accent-secondary': '#ec4899',
      '--accent-tertiary': '#d946ef',
      '--shadow-card': '0 4px 24px rgba(0, 0, 0, 0.4)',
      '--shadow-glow': 'rgba(244, 63, 94, 0.12)',
      '--sidebar-bg': 'rgba(15, 7, 10, 0.95)',
      '--sidebar-accent': '#f43f5e',
      '--gradient-primary': 'linear-gradient(135deg, #f43f5e, #e11d48)',
      '--gradient-secondary': 'linear-gradient(135deg, #ec4899, #d946ef)',
    },
  },
  cyber: {
    name: 'Cyberpunk',
    description: 'Neon multi-color electric',
    preview: ['#0a0d1f', '#1a1230', '#00ffff'],
    vars: {
      '--bg-deepest': '#050508',
      '--bg-main': '#0a0d1f',
      '--bg-card': 'rgba(20, 14, 40, 0.5)',
      '--bg-card-hover': 'rgba(30, 20, 55, 0.6)',
      '--bg-elevated': 'rgba(26, 18, 48, 0.8)',
      '--border-color': 'rgba(0, 255, 255, 0.08)',
      '--border-glow': 'rgba(0, 255, 255, 0.3)',
      '--text-primary': '#e0f0ff',
      '--text-secondary': '#8ba8c8',
      '--text-dim': '#485068',
      '--accent-primary': '#00ffff',
      '--accent-secondary': '#ff00ff',
      '--accent-tertiary': '#ffff00',
      '--shadow-card': '0 4px 24px rgba(0, 0, 0, 0.5)',
      '--shadow-glow': 'rgba(0, 255, 255, 0.15)',
      '--sidebar-bg': 'rgba(5, 5, 8, 0.95)',
      '--sidebar-accent': '#00ffff',
      '--gradient-primary': 'linear-gradient(135deg, #00ffff, #00ccff)',
      '--gradient-secondary': 'linear-gradient(135deg, #ff00ff, #cc00ff)',
    },
  },
  frost: {
    name: 'Nordic Frost',
    description: 'Ice blue with crisp white',
    preview: ['#0f1419', '#1a2128', '#60a5fa'],
    vars: {
      '--bg-deepest': '#0a0e14',
      '--bg-main': '#0f1419',
      '--bg-card': 'rgba(26, 33, 40, 0.6)',
      '--bg-card-hover': 'rgba(34, 42, 52, 0.7)',
      '--bg-elevated': 'rgba(26, 33, 40, 0.85)',
      '--border-color': 'rgba(96, 165, 250, 0.08)',
      '--border-glow': 'rgba(96, 165, 250, 0.25)',
      '--text-primary': '#e8f2ff',
      '--text-secondary': '#8ba5c8',
      '--text-dim': '#4a5768',
      '--accent-primary': '#60a5fa',
      '--accent-secondary': '#818cf8',
      '--accent-tertiary': '#a78bfa',
      '--shadow-card': '0 4px 24px rgba(0, 0, 0, 0.3)',
      '--shadow-glow': 'rgba(96, 165, 250, 0.12)',
      '--sidebar-bg': 'rgba(10, 14, 20, 0.95)',
      '--sidebar-accent': '#60a5fa',
      '--gradient-primary': 'linear-gradient(135deg, #60a5fa, #3b82f6)',
      '--gradient-secondary': 'linear-gradient(135deg, #818cf8, #6366f1)',
    },
  },
};

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => localStorage.getItem('app-theme') || 'midnight');

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem('app-theme', theme);
  }, [theme]);

  const applyTheme = (themeKey) => {
    const vars = THEMES[themeKey]?.vars;
    if (!vars) return;
    const root = document.documentElement;
    Object.entries(vars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    // Also set body background
    document.body.style.backgroundColor = vars['--bg-deepest'];
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider');
  return context;
}
